import AuthSidePanel from "../../components/Auth/AuthSidePanel";
import useTranslator from "../../hooks/useTranslator";
import { Link } from "react-router-dom";
import EmailForm from "../../components/Auth/EmailForm";
import PasswordForm from "../../components/Auth/PasswordForm";
import axios from "axios";
import EnterOtpModal from "../onboarding/enterOtpModal";
import { haveValue, toast } from "../../helper/common";
import AuthContext from "../../context/auth/AuthContext";
import { useContext, useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import LLBasicInfoRaw from "../onboarding/LLBasicInfo";
import LLOnboardingStepsRaw from "../landlord/landlord-onboarding/LLOnboardingSteps";
import { trackClarityEvent } from "../../Utils/clarityReady";
import constants from "../../Utils/constants";
import { LanguageSwitch } from "../../components/design";
import type { AuthScreen, UserLoginForm } from "./authFlow";

const LLBasicInfo: any = LLBasicInfoRaw;
const LLOnboardingSteps: any = LLOnboardingStepsRaw;

interface LandlordAuthProps {
  currentLang: string;
  currentScreen: AuthScreen;
  handleLanguageChange: (lang: string) => void;
  emailSubmitHandler: (event: React.FormEvent<HTMLFormElement>) => void;
  disabled: boolean;
  onChangeHandler: (event: React.ChangeEvent<HTMLInputElement>) => void;
  userLogin: UserLoginForm;
  setUserLogin: React.Dispatch<React.SetStateAction<UserLoginForm>>;
  setDisableButton: (value: boolean) => void;
  setCurrentScreen: (screen: AuthScreen) => void;
  validateEmail: (email: string) => boolean;
  strongPassword: RegExp;
  runTimer: () => void;
  sendOtpFun: () => void;
  otp: string;
  setOtp: (otp: string) => void;
  showResend: boolean;
  showTimer: number;
  verifyOtpHandler: (
    event: React.FormEvent<HTMLFormElement>,
    createUser?: () => Promise<void>
  ) => Promise<void>;
  redirectFun: (data: any) => void;
  userAlreadyExists: boolean;
  verifyOtpLoading: boolean;
  from?: string | null;
}

const LandlordAuth = ({
  currentLang,
  currentScreen,
  handleLanguageChange,
  emailSubmitHandler,
  disabled,
  onChangeHandler,
  userLogin,
  setUserLogin,
  setDisableButton,
  setCurrentScreen,
  validateEmail,
  strongPassword,
  runTimer,
  sendOtpFun,
  otp,
  setOtp,
  showResend,
  showTimer,
  verifyOtpHandler,
  redirectFun,
  userAlreadyExists,
  verifyOtpLoading,
  from,
}: LandlordAuthProps) => {
  const { _t } = useTranslator();
  const { resetPassword } = useContext(AuthContext);
  const [cookies] = useCookies(["workspace_invite"]);
  const [passwordError, setPasswordError] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const emailParam = params.get("email");

    if (emailParam) {
      onChangeHandler({
        target: { name: "email", value: emailParam },
      } as React.ChangeEvent<HTMLInputElement>);

      params.delete("email");
      const newUrl =
        window.location.pathname +
        (params.toString() ? "?" + params.toString() : "");
      window.history.replaceState({}, "", newUrl);
    }
  }, []);

  const loginWithCredentialsHandler = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();
    if (userLogin.email === "" || userLogin.password === "") {
      toast.error(_t("pleaseInputYourCredentials"));
      return;
    }

    try {
      setDisableButton(true);
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/users/login`,
        {
          email: userLogin.email,
          password: userLogin.password,
          type: userLogin.type,
        },
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      );

      const data = response.data;
      if (data.token) {
        if (data.user.verified) {
          redirectFun(data);
        }
      } else {
        toast.error(_t("loginFailed"));
      }
    } catch (error: any) {
      const message =
        error.response?.data?.message ||
        error.response?.data?.msg ||
        error.message;
      toast.error(haveValue(message) ? message : _t("loginFailed"));
      console.log(error);
    } finally {
      setDisableButton(false);
    }
  };

  const signupHandler = async (): Promise<void> => {
    setDisableButton(true);
    setPasswordError("");
    try {
      if (userLogin.password === "") {
        setPasswordError(_t("pleaseEnterPassword"));
      } else if (userLogin.confirmPassword === "") {
        setPasswordError(_t("pleaseEnterReEnterPassword"));
      } else if (userLogin.password !== userLogin.confirmPassword) {
        setPasswordError(_t("passwordDoesNotMatch"));
      } else if (!validateEmail(userLogin.email)) {
        setPasswordError(_t("invalidEmailAddress"));
      } else if (!strongPassword.test(userLogin.password)) {
        setPasswordError(
          _t(
            "pleaseIncludeUppercaseLowercaseAndSpecialCharactersInYourPasswordAndMustBeAtleastEightCharactersLong"
          )
        );
      } else {
        setPasswordError("");
        const registerData = {
          email: userLogin.email,
          password: userLogin.password,
          confirmPassword: userLogin.confirmPassword,
          type: userLogin.type,
          user_type: userLogin.user_type,
          qrCode_signup: userLogin?.qrCode_signup,
          fname_companyName: userLogin?.fname_companyName,
          lname: userLogin?.lname,
          phone: userLogin?.phone,
          is_edit: false,
          lang: currentLang,
          ...(cookies.workspace_invite && { isInvitedLandlord: true }),
        };
        const response = await axios.post(
          `${process.env.REACT_APP_SERVER_URL}/users/signup`,
          registerData,
          { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
        );
        const data = response.data;
        if (data) {
          localStorage.setItem("token", data.token);
          localStorage.setItem("saveduserid", data.user._id);
          setUserLogin((prev) => {
            return {
              ...prev,
              _id: data.user._id,
            };
          });
          trackClarityEvent("landlord_basic_info_step_ended");
          setTimeout(() => {
            trackClarityEvent("landlord_how_to_plan_skor_step_started");
          }, constants.CLARITY_DELAY);
        }
        redirectFun(response.data);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setDisableButton(false);
    }
  };

  const submitHandler = async (
    e: React.FormEvent,
    verifyOtp?: boolean
  ): Promise<void> => {
    try {
      e.preventDefault();
      if (!verifyOtp) {
        if (
          haveValue(userLogin?.fname_companyName) &&
          haveValue(userLogin?.lname) &&
          haveValue(userLogin?.phone) &&
          userLogin?.phone?.length > 6
        ) {
          setCurrentScreen("verifyOtp");
          sendOtpFun();
          runTimer();
        } else {
          trackClarityEvent("landlord_basic_info_step_validation_error");
          toast.error(_t("allFieldsAreMandatory"));
        }
      } else {
        await verifyOtpHandler(
          e as React.FormEvent<HTMLFormElement>,
          signupHandler
        );
      }
    } catch (error) {}
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (userLogin.password === "" || userLogin.confirmPassword === "") {
      toast.error(_t("pleaseEnterAllFields"));
    } else if (userLogin.password !== userLogin.confirmPassword) {
      toast.error(_t("passwordNotMatches"));
    } else if (!strongPassword.test(userLogin.password)) {
      toast.error(
        _t("pleaseIncludeUppercaseLowercaseAndSpecialCharactersInYourPassword")
      );
    } else {
      const payload = {
        password: userLogin?.password,
        otp,
        type: userLogin?.type,
      };
      resetPassword(payload, userLogin?.email)
        .then((res) => {
          if (res) {
            setCurrentScreen("email");
          }
        })
        .catch((err) => {
          console.log(err);
        });
    }
  };

  const initiateForgotPassword = (e: React.FormEvent) => {
    e.preventDefault();
    if (userLogin.password === "" || userLogin.confirmPassword === "") {
      setPasswordError(_t("pleaseEnterAllFields"));
    } else if (userLogin.password !== userLogin.confirmPassword) {
      setPasswordError(_t("passwordNotMatches"));
    } else if (!strongPassword.test(userLogin.password)) {
      setPasswordError(
        _t("pleaseIncludeUppercaseLowercaseAndSpecialCharactersInYourPassword")
      );
    } else {
      sendOtpFun();
      setCurrentScreen("verifyOtpForgot");
    }
  };

  const signupNextScreenRedirect = (e: React.FormEvent) => {
    e.preventDefault();
    if (userLogin.password !== userLogin.confirmPassword) {
      setPasswordError(_t("passwordNotMatches"));
    } else if (!strongPassword.test(userLogin.password)) {
      setPasswordError(
        _t(
          "pleaseIncludeUppercaseLowercaseAndSpecialCharactersInYourPasswordAndMustBeAtleastEightCharactersLong"
        )
      );
    } else {
      setCurrentScreen("landlordBasicInfo");
      trackClarityEvent("landlord_password_step_ended");
      setTimeout(() => {
        trackClarityEvent("landlord_basic_info_step_started");
      }, constants.CLARITY_DELAY);
    }
  };

  return (
    <div className="bigform lanlord-mobile-bg auth-flat">
      <AuthSidePanel role="landlord" step={2} caption={_t("stepTwoOfThree")} />
      <div className="form-right new-form-right overflow-y-scroll">
        <div className="form-inner">
          <div className="logoWrapper">
            <LanguageSwitch
              currentLang={currentLang}
              onChange={handleLanguageChange}
            />
          </div>
          {currentScreen === "email" && (
            <EmailForm
              email={userLogin?.email ?? ""}
              onChangeHandler={onChangeHandler}
              disabled={disabled}
              emailSubmitHandler={emailSubmitHandler}
              usedFor="landlord"
              from={from}
            />
          )}
          {(currentScreen === "signup" ||
            currentScreen === "login" ||
            currentScreen === "forgotPassword") && (
            <PasswordForm
              title={
                currentScreen === "forgotPassword"
                  ? _t("resetYourPassword")
                  : currentScreen === "signup"
                    ? _t("setYourPassword")
                    : _t("providePassword")
              }
              subtitle={
                currentScreen === "forgotPassword"
                  ? _t("pleaseResetAPasswordForYourAccount")
                  : currentScreen === "signup"
                    ? _t("pleaseSetAPasswordForYourAccount")
                    : _t("enterYourPasswordBelowToLoginToYourAccount")
              }
              isProvidePassword={
                currentScreen !== "signup" && currentScreen !== "forgotPassword"
              }
              passwordValue={userLogin?.password ?? ""}
              onChangePassword={onChangeHandler}
              repeatPasswordValue={userLogin.confirmPassword ?? ""}
              submitHandler={
                currentScreen === "login"
                  ? loginWithCredentialsHandler
                  : currentScreen === "forgotPassword"
                    ? initiateForgotPassword
                    : signupNextScreenRedirect
              }
              disabled={disabled}
              setCurrentScreen={setCurrentScreen}
              isTwo={
                currentScreen === "signup" || currentScreen === "forgotPassword"
              }
              inputError={passwordError}
              setInputError={setPasswordError}
              sendOtpFun={sendOtpFun}
            />
          )}
          {(currentScreen === "verifyOtp" ||
            currentScreen === "verifyOtpForgot") && (
            <EnterOtpModal
              inline
              onClose={() =>
                setCurrentScreen(userAlreadyExists ? "login" : "signup")
              }
              verifyOTPHandler={
                currentScreen === "verifyOtpForgot"
                  ? handleChangePassword
                  : (e) => submitHandler(e, true)
              }
              otp={otp}
              email={userLogin?.email ?? ""}
              setOtp={setOtp}
              showResend={showResend}
              showTimer={showTimer}
              sendOtpFun={sendOtpFun}
              verifyOtpLoading={verifyOtpLoading}
            />
          )}
          {currentScreen === "landlordBasicInfo" && (
            <LLBasicInfo
              title={_t("provideDetails", "New LL flow while signing up")}
              subtitle={_t(
                "pleaseProvideYourAccountDetailsBelow",
                "New LL flow while signing up"
              )}
              userLogin={userLogin}
              setUserLogin={setUserLogin}
              disabled={disabled}
              onChangeHandler={onChangeHandler}
              setCurrentScreen={setCurrentScreen}
              submitHandler={submitHandler}
            />
          )}
          {currentScreen === "landlordOnboarding" && (
            <LLOnboardingSteps
              userLogin={userLogin}
              setUserLogin={setUserLogin}
              disabled={disabled}
              onChangeHandler={onChangeHandler}
            />
          )}

          <p className="cookiesdiv">
            {_t(
              "byClickingNextYouAcceptAllThe",
              "Text used in onboarding process"
            )}
            <Link to="https://skortorent.com/terms-conditions.html">
              {_t("termsAndConditions", "Text used in onboarding process")}
            </Link>
            <Link to="https://skortorent.com/privacy-policy.html">
              {_t("privacyPolicy", "Text used in onboarding process")}
            </Link>
            <Link to="https://skortorent.com/cookies.html">
              {_t("cookiePolicy", "Text used in onboarding process")}
            </Link>
            <Link to="https://skortorent.com/legal-notice.html">
              {_t("legalNotice", "Text used in onboarding process")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LandlordAuth;
